"use client";
import WordpressLogin from "@/components/WordpressLogin";
import Navbar from "@/components/NavBar";
import { useSearchParams } from "next/navigation";
import { useEffect, useState, Suspense, useContext } from "react";
import { useRouter } from "next/navigation";
import JWTContext from "@/components/JWTProvider";
import UserContext from "@/components/UserProvider";
import FileMaker from "@/components/FileMaker";

function LoginContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { JWT, refreshToken } = useContext(JWTContext);
  const { setUser } = useContext(UserContext);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [autoLoginError, setAutoLoginError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthAndHandleToken = async () => {
      // Get token from URL parameter
      const token = searchParams.get('t');
      
      if (token) {
        console.log('Token found in URL:', token);
        
        // Check if user is already logged in
        const userData = sessionStorage.getItem('userData');
        const jwt = sessionStorage.getItem('jwt');
        
        if (userData && jwt) {
          // User is already logged in, redirect to proposals
          console.log('User already logged in, redirecting to proposals');
          router.push('/current-proposals');
          return;
        }

        // User needs to be auto-logged in using the token
        if (JWT) {
          await performAutoLogin(token);
        } else {
          // JWT not ready yet, wait a bit and try again
          setTimeout(() => {
            if (JWT) {
              performAutoLogin(token);
            } else {
              setAutoLoginError('Authentication system not ready. Please try again.');
              setIsCheckingAuth(false);
            }
          }, 2000);
        }
        return;
      }

      // No token, check if user is already logged in
      const userData = sessionStorage.getItem('userData');
      const jwt = sessionStorage.getItem('jwt');
      
      if (userData && jwt) {
        console.log('User already logged in, redirecting to proposals');
        router.push('/current-proposals');
        return;
      }

      // User needs to login normally
      setIsCheckingAuth(false);
    };

    checkAuthAndHandleToken();
  }, [searchParams, router, JWT]);

  const performAutoLogin = async (token: string) => {
    try {
      setIsAutoLoggingIn(true);
      setAutoLoginError(null);
      
      console.log('Performing auto-login with token:', token);
      
      // Get token details from FileMaker API to get CMS ID
      const fileMaker = new FileMaker(JWT);
      const tokenDetails = await fileMaker.getTokenDetails(token);
      
        if (!tokenDetails.success) {
          let errorMessage = tokenDetails.error || 'Invalid or expired token';
          
          // Handle used token case specially
          if (tokenDetails.tokenUsed) {
            setAutoLoginError('This login link has already been used. Please use the login form below to access your account.');
            setIsAutoLoggingIn(false);
            return;
          }
          
          // Try to refresh token if it's an authentication error
          if (tokenDetails.error?.includes('Invalid FileMaker Data API token') || tokenDetails.error?.includes('952')) {
            console.log('Token authentication failed, attempting refresh...');
            try {
              await refreshToken();
              const refreshedFileMaker = new FileMaker(JWT);
              const retryTokenDetails = await refreshedFileMaker.getTokenDetails(token);
              if (retryTokenDetails.success) {
                const cmsId = retryTokenDetails.data.cmsId;
                if (cmsId) {
                  await loginUserWithCmsId(cmsId);
                  return;
                }
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
            }
          }
          
          setAutoLoginError(errorMessage);
          setIsAutoLoggingIn(false);
          return;
        }
      
      const cmsId = tokenDetails.data.cmsId;
      const policyBoardMtgId = tokenDetails.data.policyBoardMtgId;
      const policyIdeaId = tokenDetails.data.policyIdeaId;
      
      if (!cmsId) {
        setAutoLoginError('Token is missing required information (CMS ID)');
        setIsAutoLoggingIn(false);
        return;
      }
      
      console.log('Token details retrieved, CMS ID:', cmsId, 'Policy Board Mtg ID:', policyBoardMtgId, 'Policy Idea ID:', policyIdeaId);
      
      // Login user with CMS ID and get proposal ID
      await loginUserWithCmsId(cmsId, policyBoardMtgId, policyIdeaId);
      
    } catch (err: any) {
      console.error('Error during auto-login:', err);
      setAutoLoginError('Failed to complete auto-login. Please try again.');
      setIsAutoLoggingIn(false);
    }
  };

  const loginUserWithCmsId = async (cmsId: string, policyBoardMtgId?: string, policyIdeaId?: string) => {
    try {
      console.log('Logging in user with CMS ID:', cmsId);
      
      // Search for user by CMS ID
      const fileMaker = new FileMaker(JWT);
      const searchQuery = { "CMS ID": parseInt(cmsId) };
      const userResults = await fileMaker.search(searchQuery);
      
      if (!userResults || userResults.length === 0) {
        setAutoLoginError('User not found with the provided CMS ID. Please contact support.');
        setIsAutoLoggingIn(false);
        return;
      }
      
      const user = userResults[0];
      console.log('User found:', user);
      
      // Store user data in sessionStorage
      const userData = {
        cmsId: user.fieldData["CMS ID"],
        cms_id: user.fieldData["CMS ID"], // For compatibility
        firstName: user.fieldData["First Name"],
        lastName: user.fieldData["Last Name"],
        display_name: `${user.fieldData["First Name"]} ${user.fieldData["Last Name"]}`, // For NavBar display
        email: user.fieldData["Email"],
        memberType: user.fieldData["Member Type"],
        recordId: user.recordId
      };
      
      sessionStorage.setItem('userData', JSON.stringify(userData));
      sessionStorage.setItem('jwt', JWT);
      
      // Set user in context for header display
      setUser(userData);
      router.push('/current-proposals');
    } catch (err: any) {
      console.error('Error during user login:', err);
      setAutoLoginError('Failed to complete user login. Please try again.');
      setIsAutoLoggingIn(false);
    }
  };

  if (isCheckingAuth || isAutoLoggingIn) {
    return (
      <div className="font-sans">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>{isAutoLoggingIn ? 'Logging you in automatically...' : 'Checking authentication...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (autoLoginError) {
    return (
      <div className="font-sans">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">Auto-Login Failed</h3>
            <p className="text-red-700 mb-4">{autoLoginError}</p>
            <button
              onClick={() => {
                setAutoLoginError(null);
                setIsCheckingAuth(false);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Manual Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="font-sans">
        <Navbar />
        <WordpressLogin />
      </div>
    </>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}>
      <LoginContent />
    </Suspense>
  );
}
 